import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import FollowArtistButton from './FollowArtistButton';
import FollowLabelButton from './FollowLabelButton';
import MixLikeButton from './MixLikeButton';
import LikeButton from './LikeButton';
import NotifyBellButton from './NotifyBellButton';
import CoverArt from './CoverArt';
import { coverUrl } from '../utils/coverUrl';
import { entityId } from '../utils/entityId';
import { track as trackEvent } from '../utils/analytics';
import './EntityHoverCard.scss';

// Hover preview card (SoundCloud-style) for inline artist / mix / track links.
// Wrap any inline link element; after a short hover-intent delay a small
// portal'd card opens with artwork, name, and the matching quick action
// (Follow for artists and labels, Like for mixes/tracks). The trigger's own click
// behavior is untouched — the wrapper adds no handlers to the children.
//
// Hover-only surface: on touch devices (native, mobile web) this renders the
// children untouched. CSS :hover is build-gated behind (hover: hover), but the
// open logic is JS, so it needs its own gate.
const hoverCapable = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const OPEN_DELAY_MS = 400;
const CLOSE_DELAY_MS = 250;
const CARD_WIDTH = 216;
// Position estimate before the card renders (same approach as the feed mixes
// popover): tall enough that flipping above never clips the action pill.
const CARD_EST_HEIGHT = 232;

const norm = (s) => String(s || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

// Session cache of enrichment lookups — hovering the same artist/mix twice
// must not refetch. Caches the promise so concurrent hovers coalesce; resolved
// values are mirrored in _resolvedCache so an open can use them synchronously
// (first paint already complete — no genre/artwork popping in a frame later).
const _enrichCache = new Map();
const _resolvedCache = new Map();

async function enrichArtist(token, seed) {
    const res = await window.electron.djpal.autocompleteArtist(token, seed.name);
    const results = res?.results || res?.data?.results || [];
    const id = entityId(seed);
    const match = results.find((r) => (id && (r.publicId === id || r.id === id)) || norm(r.name) === norm(seed.name));
    if (!match) return seed;
    return {
        ...seed,
        artworkUrl: seed.artworkUrl || match.artworkUrl || null,
        genres: (seed.genres && seed.genres.length ? seed.genres : match.genres) || [],
        supportingDjs: match.supportingDjs ?? null,
        mixesPerformed: match.mixesPerformed ?? null,
    };
}

async function enrichMix(token, seed) {
    // Feed items with a mix context always carry mixPublicId (feedAssembler),
    // link otherwise — id-first exact filters, same as mix navigation.
    const id = entityId(seed);
    const filters = id ? { public_id: id } : seed.link ? { link: seed.link } : null;
    if (!filters) return seed;
    const searchFn = window.electron.djpal.searchMixesLite || window.electron.djpal.searchMixes;
    const result = await searchFn(token, filters, null, { page: 1, limit: 1 });
    const data = result?.data || result || {};
    const arr = data.results || data.result || [];
    const mix = Array.isArray(arr) ? arr[0] : null;
    if (!mix) return seed;
    return {
        ...seed,
        artworkUrl: seed.artworkUrl || mix.artworkUrl || mix.artwork || mix.image || null,
        djName: seed.djName || mix.djName || mix.artist || '',
        date: seed.date || mix.date || null,
    };
}

async function enrichTrack(token, seed) {
    // sourceTrack seeds are {artist, title} only — resolve by title prefix,
    // then require an artist match so a common title can't attach the wrong
    // track's artwork/id.
    const res = await window.electron.djpal.autocompleteTrack(token, seed.title);
    const results = res?.results || res?.data?.results || [];
    const match = results.find((r) => norm(r.title) === norm(seed.title)
        && (!seed.artist || norm(r.artist).includes(norm(seed.artist)) || norm(seed.artist).includes(norm(r.artist))));
    if (!match) return seed;
    return {
        ...seed,
        publicId: entityId(seed) || match.publicId || null,
        artworkUrl: seed.artworkUrl || match.artworkUrl || null,
        artist: seed.artist || match.artist || '',
        artists: seed.artists && seed.artists.length ? seed.artists : match.artists,
    };
}

// Labels: the server's summary mode (name, logo, label_stats counts) — never the label page's lists.
async function enrichLabel(token, seed) {
    const id = entityId(seed);
    if (!id || !window.electron.djpal.getLabel) return seed;
    const res = await window.electron.djpal.getLabel(token, id, { summary: true });
    const d = res?.data;
    if (!d || res?.error) return seed;
    return {
        ...seed,
        name: seed.name || d.name || '',
        artworkUrl: seed.artworkUrl || d.artworkUrl || null,
        country: d.country || null,
        supportingDjs: d.stats?.supportingDjs ?? null,
        tracksReleased: d.stats?.tracksReleased ?? null,
    };
}

const ENRICHERS = { artist: enrichArtist, mix: enrichMix, track: enrichTrack, label: enrichLabel };

function cacheKey(type, seed) {
    const id = entityId(seed);
    return `${type}:${id || norm(seed.name || seed.title || '')}${type === 'track' ? `|${norm(seed.artist)}` : ''}`;
}

function enrich(type, token, seed) {
    const key = cacheKey(type, seed);
    if (_enrichCache.has(key)) return _enrichCache.get(key);
    const p = ENRICHERS[type](token, seed).catch(() => seed)
        .then((v) => { _resolvedCache.set(key, v); return v; });
    _enrichCache.set(key, p);
    return p;
}

// The artwork is already known (or the entity has none we can find) when the
// seed either carries it or the lookup already ran — skip the fetch entirely.
// Artists always fetch once (cached): the social-proof stats only come from
// the lookup, never from feed/page seeds.
const needsEnrichment = (type, seed) => !seed.artworkUrl
    || (type === 'mix' && !seed.djName)
    || (type === 'artist' && seed.supportingDjs == null)
    || (type === 'label' && seed.supportingDjs == null);

const EntityHoverCard = ({ type, entity, onNavigate, children }) => {
    const token = useSelector((state) => state.djpal.token);
    const [open, setOpen] = useState(false);
    // Hover-out closes fade the card (~90ms) before unmount; scroll/Escape
    // closes stay instant so a fixed-positioned card never visibly drifts.
    const [closing, setClosing] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const [data, setData] = useState(entity);
    const openTimer = useRef(null);
    const closeTimer = useRef(null);
    const fadeTimer = useRef(null);
    const cardRef = useRef(null);
    const openSeq = useRef(0);

    const clearTimers = () => {
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
        if (fadeTimer.current) { clearTimeout(fadeTimer.current); fadeTimer.current = null; }
    };

    const openAt = useCallback((triggerEl) => {
        const rect = triggerEl.getBoundingClientRect();
        const margin = 8;
        let left = rect.left + rect.width / 2 - CARD_WIDTH / 2;
        left = Math.max(margin, Math.min(left, window.innerWidth - CARD_WIDTH - margin));
        let top = rect.bottom + 8;
        if (top + CARD_EST_HEIGHT > window.innerHeight - margin) {
            top = Math.max(margin, rect.top - CARD_EST_HEIGHT - 8);
        }
        setPos({ top, left });
        // The mouseenter prefetch usually finished during the intent delay —
        // open with the complete data in the FIRST paint when it did.
        const warm = _resolvedCache.get(cacheKey(type, entity));
        setData(warm || entity);
        setClosing(false);
        setOpen(true);
        trackEvent('feed_hover_card_opened', { entity_type: type });
        const seq = ++openSeq.current;
        if (!warm && token && needsEnrichment(type, entity)) {
            enrich(type, token, entity).then((full) => {
                if (openSeq.current === seq) setData(full);
            });
        }
    }, [entity, token, type]);

    const onEnter = useCallback((e) => {
        clearTimers();
        setClosing(false);
        if (open) return;
        const el = e.currentTarget;
        // Warm the lookup during the intent delay so the card opens complete —
        // enrich() is promise-cached, so the openAt() call reuses this fetch.
        // Preloading the artwork puts it in the HTTP cache before the <img>
        // mounts, so the cover doesn't pop in after the card.
        if (token && needsEnrichment(type, entity)) {
            enrich(type, token, entity).then((full) => {
                const art = full.artworkUrl || full.artwork || full.image;
                // Same sized variant the card's <CoverArt size={96}> renders.
                if (art) { const img = new Image(); img.src = coverUrl(art, 96); }
            });
        }
        openTimer.current = setTimeout(() => openAt(el), OPEN_DELAY_MS);
    }, [open, openAt, token, type, entity]);

    const scheduleClose = useCallback(() => {
        clearTimers();
        closeTimer.current = setTimeout(() => {
            setClosing(true);
            fadeTimer.current = setTimeout(() => { setOpen(false); setClosing(false); }, 90);
        }, CLOSE_DELAY_MS);
    }, []);

    // Re-entering the trigger/card mid-fade must also cancel the fade —
    // clearing timers alone would leave the card stuck at opacity 0.
    const cancelClose = useCallback(() => { clearTimers(); setClosing(false); }, []);

    // Close on Escape or page scroll — the fixed-positioned card would drift
    // away from its trigger (same rules as the feed mixes popover).
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        const onScroll = (e) => {
            if (cardRef.current && cardRef.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [open]);

    useEffect(() => clearTimers, []);

    if (!hoverCapable) return children;

    const navigate = (e) => {
        e.stopPropagation();
        setOpen(false);
        if (onNavigate) onNavigate();
    };

    const name = type === 'artist' || type === 'label' ? data.name : data.title;
    const art = data.artworkUrl || data.artwork || data.image || null;

    const sub = (() => {
        if (type === 'artist') {
            // Social proof first (the SoundCloud follower-count analog):
            // producers show who plays them, DJs show their mix count.
            // Genres are the fallback flavor when the stats are zero/absent.
            if (data.supportingDjs > 0) {
                return `Supported by ${data.supportingDjs.toLocaleString()} DJ${data.supportingDjs === 1 ? '' : 's'}`;
            }
            if (data.mixesPerformed > 0) {
                return `${data.mixesPerformed.toLocaleString()} mix${data.mixesPerformed === 1 ? '' : 'es'}`;
            }
            const genres = (data.genres || []).filter(Boolean).slice(0, 2);
            return genres.length ? genres.join(' · ') : null;
        }
        if (type === 'label') {
            // same social proof as an artist: who plays the label, then its catalogue size
            const parts = [];
            if (data.supportingDjs > 0) parts.push(`Supported by ${data.supportingDjs.toLocaleString()} DJ${data.supportingDjs === 1 ? '' : 's'}`);
            else if (data.tracksReleased > 0) parts.push(`${data.tracksReleased.toLocaleString()} track${data.tracksReleased === 1 ? '' : 's'}`);
            if (data.country) parts.push(data.country);
            return parts.join(' · ') || null;
        }
        if (type === 'mix') {
            const dj = data.djName ? `by ${data.djName}` : null;
            const date = data.date ? new Date(data.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;
            return [dj, date].filter(Boolean).join(' · ') || null;
        }
        return data.artist || null;
    })();

    const action = (() => {
        // Bell renders only once followed/liked (never on mix likes — those
        // don't notify), so the card grows a second pill after the first click.
        if (type === 'artist') {
            return (
                <span className="like-bell-group">
                    <FollowArtistButton artist={data} prominent />
                    <NotifyBellButton entityType="artist" entity={data} from="hover_card" />
                </span>
            );
        }
        if (type === 'mix') return data.djName ? <MixLikeButton mix={data} prominent /> : null;
        if (type === 'label') {
            const label = { publicId: entityId(data), name: data.name, artworkUrl: data.artworkUrl || null };
            return label.publicId ? (
                <span className="like-bell-group">
                    <FollowLabelButton label={label} prominent from="hover_card" />
                    <NotifyBellButton entityType="label" entity={label} from="hover_card" />
                </span>
            ) : null;
        }
        return (
            <span className="like-bell-group">
                <LikeButton track={data} prominent />
                <NotifyBellButton entityType="track" entity={data} from="hover_card" />
            </span>
        );
    })();

    return (
        <span className="entity-hover-trigger" onMouseEnter={onEnter} onMouseLeave={scheduleClose}>
            {children}
            {open && ReactDOM.createPortal(
                <div
                    ref={cardRef}
                    className={`entity-hover-card${closing ? ' closing' : ''}`}
                    style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={`entity-hover-art entity-hover-art-${type}`} onClick={navigate}>
                        {art
                            ? <CoverArt src={art} size={96} eager />
                            : <span className="entity-hover-art-placeholder">&#9835;</span>}
                    </div>
                    <span className="entity-hover-name" onClick={navigate}>{name}</span>
                    {/* Always mounted: reserves its line so a late-arriving
                        genre/date can't shove the action pill down a frame in. */}
                    <span className="entity-hover-sub">{sub || ' '}</span>
                    {action && <div className="entity-hover-action">{action}</div>}
                </div>,
                document.body
            )}
        </span>
    );
};

export default EntityHoverCard;
